import { BulletinsService } from 'src/services/bulletins.service';
import { Country } from 'src/utils/enums/payment.enum';
import { DealerManagementService } from 'src/services/dealer-management.service';
import { formatDateAndTime } from 'src/utils/formatDate';
import { FULL_NAME } from 'src/config';
import { IUser } from 'src/utils/interfaces/user';
import { Language } from 'src/utils/enums/documents.enum';
import { MAX_DOCUMENT_BYTES } from 'src/utils/bulletin-document-upload';
import ModalComponent from 'src/components/ui/modal';
import Spinner from 'src/components/ui/spinner';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import React, {
    Dispatch,
    memo,
    MouseEventHandler,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import './styles.scss';

// eslint-disable-next-line no-magic-numbers
const DIRECT_UPLOAD_LIMIT_BYTES = 3 * 1024 * 1024;

enum StaticPdfNotificationType {
    GENERAL_ERROR,
    SUCCESS,
    UPLOAD_ERROR,
}

interface AccordionItemProp {
    title: string;
    index: number;
    files: StaticPdfMaintenanceFile[];
    setNotification: Function;
    setOpenModal: Dispatch<React.SetStateAction<boolean>>;
    setDocumentId: Dispatch<React.SetStateAction<string>>;
    isOpen: boolean;
    onToggle: (index: number) => void;
}

interface StaticPdfMaintenanceOption {
    Brand: string;
    Language: string;
    Country: string;
}

interface StaticPdfMaintenanceFile {
    id: string;
    modifiedBy: string;
    modifiedTime: string;
    options: StaticPdfMaintenanceOption[];
}

interface StaticPdfMaintenanceItem {
    type: string;
    files: StaticPdfMaintenanceFile[];
}

interface AccordionProps {
    data: StaticPdfMaintenanceItem[];
    setNotification: Function;
    setOpenModal: Dispatch<React.SetStateAction<boolean>>;
    setDocumentId: Dispatch<React.SetStateAction<string>>;
}

interface ModalProps {
    open: boolean;
    loading: boolean;
    errors: {
        pdf: boolean;
        length: boolean;
        empty: boolean;
    };
    documentUpload: { filename: string };
    setOpen: Dispatch<React.SetStateAction<boolean>>;
    handlerChange: Function;
    submitFile: MouseEventHandler<HTMLButtonElement>;
}

const getLocaleLabel = (country: string, language: string) => {
    if (country === Country.Canada) {
        if (language === Language.FR) return 'French(CA)';
        return 'English(CA)';
    }
    return 'English(US)';
};

const formatBrand = (brand: string) => {
    const ZERO = 0;
    const ONE = 1;
    if (brand.toLowerCase() === 'vw') return 'VW';
    return brand.charAt(ZERO).toUpperCase() + brand.slice(ONE);
};

const UploadDocumentContent: React.FC<{
    handlerChange: any;
    loading: boolean;
    errors: {
        pdf: boolean;
        length: boolean;
        empty: boolean;
    };
    documentUpload: { filename: string };
}> = memo(({ handlerChange, loading, documentUpload, errors }) => {
    const { systemAdmin } = useLocalization();
    return (
        <>
            <div
                className={`c-upload u-mt-small ${
                    loading ? 'is-loading' : null
                }`}
            >
                <input
                    className='c-upload__input'
                    type='file'
                    id='mtryi013'
                    aria-describedby='upload-description-mtryi013'
                    accept='application/pdf'
                    onChange={handlerChange}
                />
                <div className='c-upload__description'>
                    <i className='c-upload__icon' aria-hidden='true'></i>
                    <span
                        className='c-upload__description-supplementary-text'
                        id='upload-description-mtryi013'
                    >
                        {documentUpload && (documentUpload as any).filename
                            ? (documentUpload as any).filename
                            : systemAdmin.SYSTEM_ADMIN_PAGE_DESCRIPTION_LABEL}
                    </span>
                </div>
                <div className='c-upload__button c-btn' aria-hidden='true'>
                    <span>{systemAdmin.SYSTEM_ADMIN_PAGE_ADD_BTN}</span>
                </div>
                <div
                    className='c-upload__loader'
                    aria-hidden='true'
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Spinner />
                    <span className='c-upload__loader-text'>
                        {systemAdmin.SYSTEM_ADMIN_PAGE_PROCESS_LABEL}
                    </span>
                </div>
            </div>
            <div className='error-block u-mt-small'>
                {errors.length ? (
                    <p className='c-error-message'>
                        File size not allowed. Maximum allowed 10MB.
                    </p>
                ) : null}
                {errors.pdf ? (
                    <p className='c-error-message'>
                        File type not allowed. Just PDF extension is allowed.
                    </p>
                ) : null}
                {errors.empty ? (
                    <p className='c-error-message'>
                        File is empty. Please select a valid file.
                    </p>
                ) : null}
            </div>
        </>
    );
});
UploadDocumentContent.displayName = 'UploadDocumentContent';

const UploadDocumentModal: React.FC<ModalProps> = memo(
    ({
        open,
        setOpen,
        handlerChange,
        submitFile,
        loading,
        documentUpload,
        errors,
    }) => {
        const { systemAdmin } = useLocalization();
        return (
            <ModalComponent
                open={open}
                setOpen={setOpen}
                titleContent={systemAdmin.SYSTEM_ADMIN_PAGE_UPLOAD_DOC_BTN}
                bodyContent={
                    <UploadDocumentContent
                        handlerChange={handlerChange}
                        loading={loading}
                        documentUpload={documentUpload}
                        errors={errors}
                    />
                }
                footerContent={
                    <div className='c-modal__actions__container'>
                        <div className='c-modal__actions'>
                            <div className='c-modal__actions__item'>
                                <button
                                    className='c-btn'
                                    onClick={submitFile}
                                    disabled={loading}
                                >
                                    <span className='c-btn__text'>
                                        {
                                            systemAdmin.SYSTEM_ADMIN_PAGE_UPLOAD_BTN
                                        }
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                }
            />
        );
    },
);
UploadDocumentModal.displayName = 'UploadDocumentModal';

const TableRow: React.FC<{
    file: StaticPdfMaintenanceFile;
    id: string;
    setNotification: Function;
    type: string;
    setOpenModal: Dispatch<React.SetStateAction<boolean>>;
    setDocumentId: Dispatch<React.SetStateAction<string>>;
}> = memo(
    ({ file, id, setNotification, type, setOpenModal, setDocumentId }) => {
        const [isViewLoading, setIsViewLoading] = useState(false);

        const donwloadDocumentHandler = useCallback(
            async (brand: string, country: string, t: string, lang: string) => {
                const newTab = window.open('', '_blank');
                if (!newTab) return;
                setIsViewLoading(true);
                try {
                    const service = new BulletinsService();
                    const res = await service.getDocuments(
                        brand.toLowerCase(),
                        country,
                        t,
                        lang,
                    );
                    const contentType = String(
                        res.headers?.['content-type'] || '',
                    );
                    if (contentType.includes('application/json')) {
                        const text =
                            res.data instanceof Blob
                                ? await res.data.text()
                                : JSON.stringify(res.data);
                        const { downloadUrl } = JSON.parse(text);
                        if (!downloadUrl) throw new Error('No downloadUrl');
                        newTab.location.assign(downloadUrl);
                    } else {
                        const blob = new Blob([res.data], {
                            type: 'application/pdf',
                        });
                        // Not revoked: the PDF viewer re-reads this URL on download/print.
                        newTab.location.assign(URL.createObjectURL(blob));
                    }
                } catch (e) {
                    newTab.close();
                    setNotification(StaticPdfNotificationType.GENERAL_ERROR);
                } finally {
                    setIsViewLoading(false);
                }
            },
            [setNotification],
        );

        const uploadDocumentHandler = useCallback(
            (docId: string) => {
                setOpenModal(true);
                setDocumentId(docId);
            },
            [setOpenModal],
        );

        const { systemAdmin } = useLocalization();

        return (
            <>
                {file.options.map((option, idx) => (
                    <tr key={`${file.id}-${idx}`}>
                        <td data-label='Brand'>{formatBrand(option.Brand)}</td>
                        <td data-label='Country'>
                            {getLocaleLabel(option.Country, option.Language)}
                        </td>
                        <td data-label='Modified by'>{file.modifiedBy}</td>
                        <td data-label='Modified date'>
                            {formatDateAndTime(file.modifiedTime)}
                        </td>
                        <td data-label='View'>
                            {isViewLoading ? (
                                <div className='loader_table'>
                                    <Spinner />
                                </div>
                            ) : (
                                <button
                                    className='u-base-link'
                                    onClick={() =>
                                        donwloadDocumentHandler(
                                            option.Brand,
                                            option.Country,
                                            type,
                                            option.Language,
                                        )
                                    }
                                >
                                    {systemAdmin.SYSTEM_ADMIN_PAGE_VIEW_BTN}
                                </button>
                            )}
                        </td>
                        <td data-label='Upload'>
                            <button
                                className='u-base-link'
                                onClick={() => uploadDocumentHandler(id)}
                            >
                                {systemAdmin.SYSTEM_ADMIN_PAGE_UPLOAD_BTN}
                            </button>
                        </td>
                    </tr>
                ))}
            </>
        );
    },
);
TableRow.displayName = 'TableRow';

const ContentTable: React.FC<{
    files: StaticPdfMaintenanceFile[];
    setNotification: Function;
    type: string;
    setOpenModal: Dispatch<React.SetStateAction<boolean>>;
    setDocumentId: Dispatch<React.SetStateAction<string>>;
}> = memo(({ files, setNotification, type, setOpenModal, setDocumentId }) => {
    const { systemAdmin } = useLocalization();
    return (
        <div className='c-table-wrapper c-table--wide'>
            <table role='table'>
                <thead>
                    <tr role='row'>
                        <th scope='col'>
                            {systemAdmin.SYSTEM_ADMIN_PAGE_BRAND_LABEL}
                        </th>
                        <th scope='col'>
                            {systemAdmin.SYSTEM_ADMIN_PAGE_COUNTRY_LABEL}
                        </th>
                        <th scope='col'>
                            {systemAdmin.SYSTEM_ADMIN_PAGE_BY_LABEL}
                        </th>
                        <th scope='col'>
                            {systemAdmin.SYSTEM_ADMIN_PAGE_DATE_LABEL}
                        </th>
                        <th scope='col'>
                            {systemAdmin.SYSTEM_ADMIN_PAGE_VIEW_BTN}
                        </th>
                        <th scope='col'>
                            {systemAdmin.SYSTEM_ADMIN_PAGE_UPLOAD_BTN}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {files.map((file: StaticPdfMaintenanceFile) => (
                        <TableRow
                            file={file}
                            key={file.id}
                            id={file.id}
                            setNotification={setNotification}
                            type={type}
                            setOpenModal={setOpenModal}
                            setDocumentId={setDocumentId}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
});
ContentTable.displayName = 'ContentTable';

const AccordionItem: React.FC<AccordionItemProp> = memo(
    ({
        title,
        index,
        files,
        setNotification,
        setOpenModal,
        setDocumentId,
        isOpen,
        onToggle,
    }) => {
        const splitPascalCase = useCallback((type: string) => {
            if (type === 'CDEmenuLTEform') {
                const words = 'LeaseTermExtention'.match(
                    /([A-Z]+(?=[A-Z][a-z])|[A-Z][a-z]+|[A-Z]+)/g,
                );
                return words ? words.join(' ') : type;
            }
            const words = type.match(
                /([A-Z]+(?=[A-Z][a-z])|[A-Z][a-z]+|[A-Z]+)/g,
            );
            return words ? words.join(' ') : type;
        }, []);
        return (
            <>
                <div
                    className={`c-accordion__header js-accordion__title ${
                        isOpen ? 'is-active' : ''
                    }`}
                    onClick={() => onToggle(index)}
                >
                    <span className='c-accordion__title-icon'></span>
                    <h3 className='c-accordion__title'>
                        <span
                            className='c-accordion__title-label'
                            aria-expanded={isOpen}
                            role='button'
                            tabIndex={index}
                        >
                            <span className='c-accordion__title-label-text'>
                                {splitPascalCase(title)}
                            </span>
                        </span>
                    </h3>
                </div>
                <div
                    className={`c-accordion__panel ${
                        isOpen ? 'is-expanded' : 'is-collapsed'
                    }`}
                    aria-hidden={!isOpen}
                >
                    {isOpen && (
                        <div className='c-accordion__content'>
                            <ContentTable
                                files={files}
                                setNotification={setNotification}
                                type={title}
                                setOpenModal={setOpenModal}
                                setDocumentId={setDocumentId}
                            />
                        </div>
                    )}
                </div>
            </>
        );
    },
);
AccordionItem.displayName = 'AccordionItem';

const Accordion: React.FC<AccordionProps> = ({
    data,
    setNotification,
    setOpenModal,
    setDocumentId,
}) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const sortedData = useMemo(
        () => [...data].sort((a, b) => a.type.localeCompare(b.type)),
        [data],
    );
    const handleToggle = useCallback((idx: number) => {
        setOpenIndex((current) => (current === idx ? null : idx));
    }, []);
    return (
        <div className='c-form-section__content'>
            <div
                className='c-accordion js-accordion'
                data-tr-component='Accordion'
            >
                {sortedData.map((item, index) => (
                    <AccordionItem
                        title={item.type}
                        index={index}
                        files={item.files}
                        key={item.type}
                        setNotification={setNotification}
                        setOpenModal={setOpenModal}
                        setDocumentId={setDocumentId}
                        isOpen={openIndex === index}
                        onToggle={handleToggle}
                    />
                ))}
            </div>
        </div>
    );
};

const StaticPdfMaintenance: React.FC = () => {
    const emptyNotification = {
        content: '',
        type: NotificationType.SUCCESS,
    };
    const { labelField, systemAdmin } = useLocalization();
    const [isLoading, setIsLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [visibility, setVisibility] = useState(false);
    const [showNotification, setShowNotification] = useState(emptyNotification);
    const [data, setData] = useState<StaticPdfMaintenanceItem[]>([]);
    const [openModal, setOpenModal] = useState(false);

    const [documentUpload, setDocument] = useState({} as any);
    const [documentId, setDocumentId] = useState('');

    const [showErrors, setShowErrors] = useState({
        pdf: false,
        length: false,
        empty: false,
    });

    useEffect(() => {
        if (openModal === false) {
            setSubmitLoading(false);
            setDocument({} as any);
        }
    }, [openModal]);

    async function getAllDocumentsRequest() {
        try {
            setIsLoading(true);
            const service = new DealerManagementService();
            const docType = 3;
            const res = await service.getAllDocuments(docType);
            setData(res.data);
        } catch (e: any) {
            setNotification(StaticPdfNotificationType.GENERAL_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    const getNotification = () => {
        return (
            <Notification
                content={showNotification.content}
                type={showNotification.type}
                setVisibility={setVisibility}
                visibility={visibility}
            />
        );
    };

    useEffect(() => {
        getAllDocumentsRequest();
    }, []);

    function setNotification(
        type: StaticPdfNotificationType,
        successMessage?: string,
    ) {
        let notificationMessage, notificationType;
        switch (type) {
            case StaticPdfNotificationType.GENERAL_ERROR:
                notificationMessage =
                    labelField.LABEL_FIELD_ERROR_REQUEST_SERVICE;
                notificationType = NotificationType.ERROR;
                break;
            case StaticPdfNotificationType.SUCCESS:
                notificationMessage = `${successMessage} ${systemAdmin.SYSTEM_ADMIN_PAGE_SUCC_MSG}`;
                notificationType = NotificationType.SUCCESS;
                break;
            case StaticPdfNotificationType.UPLOAD_ERROR:
                notificationMessage = systemAdmin.SYSTEM_ADMIN_PAGE_ERR_MSG;
                notificationType = NotificationType.ERROR;
                break;
        }
        setShowNotification({
            type: notificationType,
            content: notificationMessage,
        });
        setVisibility(true);
    }

    const toBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setShowErrors({
            length: false,
            pdf: false,
            empty: false,
        });
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > MAX_DOCUMENT_BYTES) {
            setShowErrors(() => {
                return { length: true, pdf: false, empty: false };
            });
            e.target.value = '';
            setDocument({ filename: '', content: '' });
            return;
        }

        const isPdfMime = file.type === 'application/pdf';
        const hasPdfExt = /\.pdf$/i.test(file.name);

        const buffer = await file.slice(Number('0'), Number('5')).arrayBuffer();
        const header = String.fromCharCode(...new Uint8Array(buffer));
        const isPdfHeader = header === '%PDF-';

        if (!isPdfMime || !hasPdfExt || !isPdfHeader) {
            setShowErrors(() => {
                return { length: false, pdf: true, empty: false };
            });
            e.target.value = '';
            setDocument({ filename: '', content: '' });
            return;
        }

        const base64 = await toBase64(file);
        const content = base64.split(',')[1];
        setDocument({
            filename: file.name,
            content,
        });
    };

    const fullName = useSelector((state: IUser) => {
        return state.fullName;
    });

    function base64ToFile(
        base64: string,
        filename: string,
        mime = 'application/pdf',
    ): File {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return new File([bytes], filename, { type: mime });
    }

    const submitFile = async () => {
        if (
            Object.keys(documentUpload).length === Number('0') ||
            documentUpload.filename === ''
        ) {
            setShowErrors({
                length: false,
                pdf: false,
                empty: true,
            });

            return;
        }

        try {
            setSubmitLoading(true);
            setVisibility(false);

            const service = new BulletinsService();

            const uploadedBy =
                fullName || sessionStorage.getItem(FULL_NAME)?.toString() || '';

            const file = base64ToFile(
                (documentUpload as any).content,
                (documentUpload as any).filename,
                'application/pdf',
            );

            if (file.size <= DIRECT_UPLOAD_LIMIT_BYTES) {
                // Direct upload for small files (≤ 3MB).
                await service.uploadDocument(
                    documentId,
                    Number('3'),
                    uploadedBy,
                    file,
                );
            } else {
                // S3 presigned URL upload for large files (> 3MB).
                const presigned = await service.getUploadPresignedUrl(
                    documentId,
                    Number('3'),
                    file.name,
                );
                await fetch(presigned.data.uploadUrl, {
                    method: 'PUT',
                    body: file,
                    headers: {
                        'Content-Type': 'application/pdf',
                        'x-amz-server-side-encryption': 'aws:kms',
                        'x-amz-server-side-encryption-aws-kms-key-id':
                            presigned.data.kmsKeyId || '',
                    },
                });
                await service.uploadDocumentWithS3Key(
                    documentId,
                    Number('3'),
                    uploadedBy,
                    presigned.data.s3Key,
                );
            }

            const doc = data
                .flatMap((item) => item.files || [])
                .find((item) => item.id === documentId);

            const options = doc?.options[0];

            setNotification(
                StaticPdfNotificationType.SUCCESS,
                `${formatBrand((options as any)?.Brand)} - ${getLocaleLabel(
                    (options as any)?.Country,
                    (options as any)?.Language,
                )}`,
            );
        } catch (err) {
            console.error('Document upload failed', err);

            setNotification(StaticPdfNotificationType.UPLOAD_ERROR);
        } finally {
            setOpenModal(false);
            setSubmitLoading(false);
            setDocument({} as any);
            getAllDocumentsRequest();
        }
    };

    return (
        <div className='dashboard-content-wrapper'>
            <div className='dashboard-content-container'>
                <section className='o-content-section'>
                    <div className='static-pdf-container'>
                        {visibility ? getNotification() : null}
                        <h5>
                            <span className='c-section-heading__title-text'>
                                {systemAdmin.STATIC_PDF_MAINTENANCE}
                            </span>
                        </h5>
                        <section className={'c-form-section'}>
                            {isLoading ? (
                                <div className='loader_container'>
                                    <Spinner />
                                </div>
                            ) : (
                                <>
                                    <div className='info-container c-form-section__content'>
                                        <p>
                                            {
                                                systemAdmin.PAGE_STATIC_PDF_MAINTENANCE_INFO
                                            }
                                        </p>
                                        <p>
                                            {
                                                systemAdmin.PAGE_STATIC_PDF_MAINTENANCE_INFO2
                                            }
                                        </p>
                                    </div>
                                    <Accordion
                                        data={data}
                                        setNotification={setNotification}
                                        setOpenModal={setOpenModal}
                                        setDocumentId={setDocumentId}
                                    />
                                </>
                            )}
                        </section>
                    </div>
                    {openModal && (
                        <UploadDocumentModal
                            open={openModal}
                            loading={submitLoading}
                            documentUpload={documentUpload}
                            setOpen={setOpenModal}
                            handlerChange={handleFileChange}
                            submitFile={submitFile}
                            errors={showErrors}
                        />
                    )}
                </section>
            </div>
        </div>
    );
};

export default StaticPdfMaintenance;
